import React from 'react';
import {Box, Grid, TextField, Typography, Button, Autocomplete, List, ListItem} from "@mui/material";
import swal from "sweetalert";
import {saveAdvisorClientMapping, getAllClientAdvisorsInfo, getSingleUser, getAllAdvisorsUsernames} from '../../../utilityFunctions/WellnessUtil';
import Colors from "../../../components/ui/Colors";
import { loaderAnimation } from '../../../Api';
import { useHistory } from "react-router-dom";

/**
 * Initial advisor request object with empty advisor username.
 * Used to initialize the new advisor request data structure.
 */
const newAdvisorRequest={
    advisorUsername: ""
};

/**
 * Component for mapping consumers to advisors.
 * Allows a consumer to search for and select an advisor to add to their profile.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.loginUser - The currently logged in user object with authentication token
 * @returns {JSX.Element} The rendered consumer-advisor mapping interface
 */
export default function ConsumerAdvisorMapping(props) {

    const {loginUser} = props;

    let history = useHistory();

    const [loading, setLoading] = React.useState(false);

    const[usernames, setUsernames] = React.useState(null);
    const[username, setUsername] = React.useState(null);

    React.useEffect(() => {
        /**
         * Asynchronous function to load initial values when component mounts.
         * Calls the initialValues function to fetch advisor usernames.
         * 
         * @async
         */
        const loadInitialValues = async () => {
            await initialValues();
        }
        loadInitialValues();
    }, []);

    /**
     * Fetches the list of all advisor usernames from the server.
     * Updates the usernames state with the fetched data.
     * Displays an error message if the fetch fails.
     * 
     * @async
     */
    const initialValues = async () => {
        
        setLoading(true);
        const response = await getAllAdvisorsUsernames(loginUser.jwtToken);
        setLoading(false);

        if(!response){
            swal({
                title: "Error",
                text: "Failed to get advisors",
                icon: "error",
                button: "OK",
            });
            return;
        }

        setUsernames(response);
    };

    /**
     * Handles changes in the username autocomplete selection.
     * Updates the username state and the newAdvisorRequest object with the selected username.
     * 
     * @async
     * @param {Object} e - The event object
     * @param {Object} value - The selected advisor value from the autocomplete
     */
    const usernameChange = async (e, value) => {
        if(!value){
            return;
        }

        setUsername(value);
        newAdvisorRequest.advisorUsername = value.username;

    };

    /**
     * Displays a confirmation dialog before adding a new advisor.
     * Calls addNewAdvisor if the user confirms the action.
     */
    const askNewAdvisor = () => {
        swal({
            title: "Adding Advisor",
            text: "Are you sure you want to add this advisor?",
            icon: "info",
            button: "OK",
        }).then(() => {
            addNewAdvisor(username);
            return;
        });
    };

    /**
     * Saves the advisor-client mapping to the server.
     * Displays appropriate success/error messages based on the response.
     * Redirects to the LLM choice page on success.
     * 
     * @async
     */
    const addNewAdvisor = async () => {
        setLoading(true);
        const response = await saveAdvisorClientMapping(newAdvisorRequest, loginUser.jwtToken);
        setLoading(false);

        if(!response){
            swal({
                title: "Error",
                text: "Failed to add advisor",
                icon: "error",
                button: "OK",
            });
            return;
        };

        swal({
            title: "Success",
            text: "Advisor added successfully",
            icon: "success",
            button: "OK",
        }).then(() => {
            history.push("/consumer/llm_choose");
        });
    }

    return (
        <Box sx={{display: "flex", justifyContent: "center", width: "100%", height: "100%"}}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "95%",
                    height: "90%",
                    textAlign: "center",
                    marginTop: {xs: "1.3rem", sm: 0},
                }}
            >
                {loading ? loaderAnimation :
                    <Grid container direction="row" spacing={1} sx={{justifyContent: "center"}}>
                    {usernames &&
                            <>
                            <Grid item xs={12} sx={{display: "flex", justifyContent: "center"}}>
                                <Autocomplete
                                    sx={{width: {xs: "90%", sm: "80%", md: "60%"}}}
                                    options={usernames}
                                    getOptionLabel={(option) => option.username}
                                    clearOnEscape
                                    autoComplete = {true}
                                    noOptionsText = {"username not found"}
                                    fullWidth
                                    onChange={usernameChange}
                                    renderInput={(params) => (
                                        <TextField  {...params} label="Username" variant="standard" />
                                    )}
                                />
                            </Grid>
                            {username &&
                            <Grid item xs={12} sm={12} md={12}>
                                <Button onClick={askNewAdvisor} variant="contained" sx={{backgroundColor: Colors.primary, marginTop: "2rem",}}>
                                    Add Advisor
                                </Button>
                            </Grid>
                            }
                            </>
                    }
                    </Grid>
                }
            </Box>
        </Box>
    );
}
